import { Show, createSignal, type Component } from 'solid-js';

import { ApiError, login, type Account } from '../net/account';

/**
 * The sign-in screen.
 *
 * ── What it does not do ─────────────────────────────────────────────────────
 *
 * **There is no sign-up link, and that is the design rather than an omission.** Accounts are
 * created by an operator with `grind-admin`; an open registration endpoint is a bot's front
 * door, and this game has no email verification, no captcha and no abuse tooling to put
 * behind one. The screen says so, because a login form with no way to get an account and no
 * explanation reads as broken.
 *
 * **It stores nothing.** The session is an `HttpOnly` cookie the browser attaches on its
 * own — this code cannot read it and does not try, which is the property that makes an XSS
 * unable to walk away with a session. There is no token in this component, no
 * `localStorage`, and nothing to clear on logout beyond asking the server.
 *
 * ── The one message ─────────────────────────────────────────────────────────
 *
 * Whatever the server says, verbatim. It deliberately answers *that username and password
 * do not match* for both a wrong password and an account that does not exist — telling them
 * apart would let somebody enumerate valid accounts and only then start guessing passwords.
 * A client that tried to be more helpful would give that away.
 */
export const LoginScreen: Component<{ onSignedIn: (account: Account) => void }> = (props) => {
  const [username, setUsername] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [error, setError] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);

  const submit = async (event: Event) => {
    event.preventDefault();
    if (busy()) return;

    setBusy(true);
    setError(null);
    try {
      props.onSignedIn(await login(username(), password()));
    } catch (err) {
      // The server's own sentence. A rate-limit answer in particular has to reach the
      // player intact — "wait a few minutes" is the only thing that helps, and a generic
      // failure message would have them retyping a password that is correct.
      setError(err instanceof ApiError ? err.message : 'The server could not be reached.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="gate" data-testid="login-screen">
      <form class="plate plate--riveted gate__panel" onSubmit={submit}>
        <h1 class="gate__title">G.R.I.N.D.</h1>
        <p class="gate__lede">Sign in to reach your characters.</p>

        <label class="gate__field">
          <span>Username</span>
          <input
            type="text"
            data-testid="login-username"
            autocomplete="username"
            // Focused on mount: this is the first thing on screen and the only thing to do.
            ref={(el) => queueMicrotask(() => el.focus())}
            value={username()}
            onInput={(e) => setUsername(e.currentTarget.value)}
          />
        </label>

        <label class="gate__field">
          <span>Password</span>
          <input
            type="password"
            data-testid="login-password"
            autocomplete="current-password"
            value={password()}
            onInput={(e) => setPassword(e.currentTarget.value)}
          />
        </label>

        {/* `role="alert"` because it appears in response to something the player just did
            and they may not be looking at this corner of the form. */}
        <Show when={error()}>
          {(message) => (
            <p class="gate__error" data-testid="login-error" role="alert">
              {message()}
            </p>
          )}
        </Show>

        <button
          type="submit"
          class="sigil-button gate__submit"
          data-testid="login-submit"
          disabled={busy() || username() === '' || password() === ''}
        >
          <span>{busy() ? 'Signing in…' : 'Sign In'}</span>
        </button>

        <p class="gate__note" data-testid="login-note">
          There is no public registration. Accounts are created by the server operator — ask
          them for one.
        </p>
      </form>
    </div>
  );
};
